import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useFileUpload } from "@hooks/useFileUpload";
import { gql } from "@apollo/client";
import {
  useUploadFileCreateSearchDocumentMutation,
  useUploadFileCreateMediaMutation,
} from "./useUploadFileAndCreateSearchDoc.generated";
import { MediaFragment } from "@src/fragments.generated";

export interface FileUpload {
  uuid: string;
  state: "uploading" | "success" | "error";
  fileName: string;
  file?: File;
  media?: MediaFragment;
  error?: string;
}

export interface UseUploadFileAndCreateSearchRet {
  uploadFiles: (files: File[]) => void;
}

export function useUploadFileAndCreateSearchDoc({
  onUploadStarted,
  onUploadCompleted,
  onUploadError,
}: {
  onUploadStarted: (fileUpload: FileUpload) => void;
  onUploadCompleted: (fileUpload: FileUpload) => void;
  onUploadError: (fileUpload: FileUpload) => void;
}): UseUploadFileAndCreateSearchRet {
  const [createMedia] = useUploadFileCreateMediaMutation();
  const [createSearchDocument] = useUploadFileCreateSearchDocumentMutation();
  const [uploadFile] = useFileUpload(() => {});
  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files || files.length === 0) return;
      const file = files[0]; // Only handle the first file
      const uuid = uuidv4();
      const uploadingFile: FileUpload = {
        uuid,
        state: "uploading",
        fileName: file.name,
        file: file,
      };

      onUploadStarted(uploadingFile);
      const uploadRes = await uploadFile(file);
      if (!uploadRes?.data?.uploadFile.success) {
        onUploadError({
          ...uploadingFile,
          state: "error",
          error: "Upload failed",
        });
        return;
      }
      const fileUrl = uploadRes.data.uploadFile.fileUrl;
      if (!fileUrl) {
        onUploadError({
          ...uploadingFile,
          state: "error",
          error: "No file url returned",
        });
        return;
      }
      const { data } = await createMedia({
        variables: {
          input: {
            mediaUrls: { en: fileUrl },
          },
        },
      });
      if (!data?.createMedia?.success || !data.createMedia.media) {
        const errorFile: FileUpload = {
          ...uploadingFile,
          file: undefined,
          state: "error",
          error: data?.createMedia?.error?.code || "Media creation failed",
        };
        onUploadError(errorFile);
        return;
      }
      const mediaId = data.createMedia.media.id;
      const { data: searchDocumentData } = await createSearchDocument({
        variables: {
          input: {
            mediaId: mediaId,
          },
        },
      });
      if (
        !searchDocumentData?.createSearchDocument?.success ||
        !searchDocumentData.createSearchDocument.searchDocument
      ) {
        const errorFile: FileUpload = {
          ...uploadingFile,
          file: undefined,
          state: "error",
          error:
            searchDocumentData?.createSearchDocument?.error?.code ||
            "Search document creation failed",
        };
        onUploadError(errorFile);
        return;
      }
      onUploadCompleted({
        ...uploadingFile,
        media: data.createMedia.media,
        state: "success",
      });
    },
    [
      createMedia,
      createSearchDocument,
      onUploadCompleted,
      onUploadError,
      onUploadStarted,
      uploadFile,
    ],
  );

  return {
    uploadFiles,
  };
}

gql`
  mutation UploadFileCreateMedia($input: MediaInput!) {
    createMedia(input: $input) {
      success
      error {
        code
      }
      media {
        ...Media
      }
    }
  }
  mutation UploadFileCreateSearchDocument($input: CreateSearchDocumentInput!) {
    createSearchDocument(input: $input) {
      success
      searchDocument {
        id
      }
      error {
        code
      }
    }
  }
`;
